import React from 'react';
import { Tag } from 'selfly-ui-react';

/**
 * The "Eligible consumers" row of a discount's detail block.
 *
 * Shared by all three discount types rather than repeated in each, because the truncation is the
 * only part of that block with real logic in it.
 *
 * An empty `userIds` renders "All", matching how the block already treats empty locations, cabinets
 * and products — for a discount, no user restriction genuinely does mean everyone.
 *
 * `rule.userIds` holds legacy **userKeys**, so rows are matched on `userKey`, not `_id`. A key with
 * no matching record is shown as such instead of being dropped: it is the same class of
 * "referenced thing no longer exists" that the sibling rows already surface, and hiding it would
 * make a restricted discount look narrower than it is.
 *
 * @param {object} props
 * @param {function} props.t i18n translator
 * @param {Array<string>|undefined} props.userIds legacy userKeys the discount is restricted to
 * @param {Array<object>} props.userKeys rows from `state.userKeys.userKeys`
 * @param {number} [props.limit] how many to show before collapsing the rest
 */
export default function EligibleConsumers({ t, userIds, userKeys, limit = 5 }) {
    const restrictedTo = Array.isArray(userIds) ? userIds : [];

    const label = userKey => {
        const record = (userKeys || []).find(k => k && k.userKey === userKey);
        if (!record) {
            return { text: `${userKey} (${t('doesNotExist')})`, missing: true };
        }
        return { text: record.displayName || record.userKey, missing: false };
    };

    if (restrictedTo.length === 0) {
        return (
            <div>
                <span>{t('userRestrictTitle')}: </span>
                <Tag>{t('all')}</Tag>
            </div>
        );
    }

    const shown = restrictedTo.slice(0, limit);
    const hidden = restrictedTo.slice(limit);

    return (
        <div>
            <span>{t('userRestrictTitle')}: </span>
            {shown.map((userKey, index) => {
                const { text, missing } = label(userKey);
                return (
                    <Tag className="mr-2" key={`${userKey}-${index}`} type={missing ? 'negative' : 'positive'}>
                        {text}
                    </Tag>
                );
            })}
            {hidden.length > 0 && (
                // Collapsed rather than truncated: the names are on the tooltip, so a long
                // restriction stays inspectable without the row wrapping over several lines.
                <Tag className="mr-2" title={`${hidden.length}: ${hidden.map(k => label(k).text).join(', ')}`}>
                    …
                </Tag>
            )}
        </div>
    );
}
